// Client API minimaliste — fetch wrapper avec credentials includes (httpOnly cookies).

const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? '/api';

export class ApiError extends Error {
  status: number;
  data: any;
  constructor(status: number, message: string, data?: any) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

// Mutualisation du refresh : si plusieurs requêtes prennent 401 en parallèle,
// on ne déclenche qu'un seul appel /auth/refresh.
let pendingRefresh: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = (async () => {
    try {
      const res = await fetch(API_BASE + '/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      pendingRefresh = null;
    }
  })();
  return pendingRefresh;
}

async function rawRequest(path: string, init: RequestInit): Promise<Response> {
  return fetch(API_BASE + path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

async function request<T>(path: string, init: RequestInit = {}, _retried = false): Promise<T> {
  let res = await rawRequest(path, init);
  // Auto-refresh sur 401 : on tente un refresh transparent puis on retry une fois.
  // Exclu : la route refresh elle-même (sinon boucle), et les routes auth d'entrée
  // (login/register/me — un 401 sur me veut dire "pas connecté", pas "session expirée").
  const isAuthRoute = path.startsWith('/auth/');
  if (res.status === 401 && !_retried && !isAuthRoute) {
    const ok = await tryRefresh();
    if (ok) {
      res = await rawRequest(path, init);
    }
  }
  let body: any = null;
  if (res.status !== 204) {
    const text = await res.text();
    body = text ? JSON.parse(text) : null;
  }
  if (!res.ok) {
    throw new ApiError(res.status, body?.detail ?? res.statusText, body);
  }
  return body as T;
}

export const api = {
  get:    <T = any>(path: string)               => request<T>(path),
  post:   <T = any>(path: string, body?: any)   => request<T>(path, { method: 'POST',   body: body !== undefined ? JSON.stringify(body) : undefined }),
  put:    <T = any>(path: string, body?: any)   => request<T>(path, { method: 'PUT',    body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch:  <T = any>(path: string, body?: any)   => request<T>(path, { method: 'PATCH',  body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T = any>(path: string)               => request<T>(path, { method: 'DELETE' }),
};

// Type-safe API endpoints
export interface ApiUser {
  id: number;
  username: string;
  email: string;
  role: 'super_admin' | 'admin' | 'manager' | 'user';
  is_active: boolean;
  must_change_password: boolean;
  avatar_url?: string | null;
  created_at: string;
  last_login?: string | null;
}

export const auth = {
  register: (username: string, email: string, password: string) =>
    api.post<ApiUser>('/auth/register', { username, email, password }),
  login: (username_or_email: string, password: string) =>
    api.post<ApiUser>('/auth/login', { username_or_email, password }),
  refresh: () => api.post<ApiUser>('/auth/refresh'),
  logout: () => api.post('/auth/logout'),
  me: () => api.get<ApiUser>('/auth/me'),
  changePassword: (current_password: string, new_password: string) =>
    api.post('/auth/change-password', { current_password, new_password }),
  passwordResetRequest: (email: string) =>
    api.post('/auth/password-reset/request', { email }),
  passwordResetConfirm: (token: string, new_password: string) =>
    api.post('/auth/password-reset/confirm', { token, new_password }),
};

export const usersApi = {
  list: () => api.get<ApiUser[]>('/users'),
  changeRole: (userId: number, role: ApiUser['role']) =>
    api.patch<ApiUser>(`/users/${userId}/role`, { role }),
  toggleActive: (userId: number, isActive: boolean) =>
    api.patch<ApiUser>(`/users/${userId}/active?is_active=${isActive}`),
  remove: (userId: number) => api.delete(`/users/${userId}`),
};

export interface StatsOverview {
  total_users: number;
  active_users: number;
  total_logins: number;
  logins_last_7d: number;
  total_games: number;
  games_last_7d: number;
  games_by_transport: Record<string, number>;
  games_by_mode: Record<string, number>;
}
export interface PlayerGameStat {
  user_id: number | null;
  username: string | null;
  total_games: number;
  by_transport: Record<string, number>;
  by_mode: Record<string, number>;
}
export interface CountryStat { country: string | null; country_code: string | null; count: number }
export interface CityStat    { city: string | null; country_code: string | null; latitude: string | null; longitude: string | null; count: number }
export interface TimelinePoint { date: string; count: number }

export interface PropositionDrawStat {
  proposition_id: string;
  label: string | null;
  difficulty: string | null;
  count: number;
}
export interface QuestionDrawStat {
  question_id: string;
  prompt: string | null;
  type: string | null;
  category: string | null;
  total_drawn: number;
  last_drawn_at: string | null;
  propositions: PropositionDrawStat[];
}

export const statsApi = {
  overview:    () => api.get<StatsOverview>('/stats/overview'),
  gamesByPlayer: () => api.get<PlayerGameStat[]>('/stats/games/by-player'),
  byCountry:   () => api.get<CountryStat[]>('/stats/connections/by-country'),
  byCity:      () => api.get<CityStat[]>('/stats/connections/by-city'),
  timeline:    (days = 30) => api.get<TimelinePoint[]>(`/stats/connections/timeline?days=${days}`),
  logGame:     (data: {
    mode?: string; transport?: string; player_count?: number;
    sets_played?: number; rounds_played?: number; duration_ms?: number;
  }) => api.post('/stats/games/log', data),
  logGameWinners: (data: { transport: string; is_public?: boolean; winner_user_ids: number[] }) =>
    api.post('/stats/games/winners', data),
  logDraw:     (questionId: string, pickedPropositionIds: string[]) =>
    api.post('/stats/questions/draw', { question_id: questionId, picked_proposition_ids: pickedPropositionIds }),
  questionDraws: () => api.get<QuestionDrawStat[]>('/stats/questions/draws'),
  resetQuestionDraws: () => api.delete('/stats/questions/draws'),
};

// ---------------- Rooms (multi-device & online) ----------------

export interface RoomPlayer {
  user_id: number;
  username: string;
  avatar_url: string | null;
  is_host: boolean;
  connected: boolean;
  score: number;
}
export interface RoomState {
  code: string;
  host_user_id: number;
  state: 'lobby' | 'playing' | 'finished';
  transport: 'LOCAL_MULTI_DEVICE' | 'ONLINE';
  is_public: boolean;
  room_name: string;
  players: RoomPlayer[];
  settings: Record<string, any>;
  created_at: string;
}
export interface PublicRoom {
  code: string;
  room_name: string;
  host_username: string | null;
  players_count: number;
  max_players: number;
  created_at: string;
}

export const roomsApi = {
  create: (
    transport: RoomState['transport'],
    settings: Record<string, any> = {},
    opts: { is_public?: boolean; room_name?: string } = {},
  ) =>
    api.post<RoomState>('/rooms', {
      transport, settings,
      is_public: opts.is_public ?? false,
      room_name: opts.room_name ?? '',
    }),
  join: (code: string) => api.post<RoomState>(`/rooms/${code.toUpperCase()}/join`, {}),
  get:  (code: string) => api.get<RoomState>(`/rooms/${code.toUpperCase()}`),
  listPublic: () => api.get<PublicRoom[]>('/rooms'),
};

/** Construit l'URL WebSocket de la room en se basant sur l'origine courante.
 *  En prod, Caddy proxifie /ws/* vers le backend FastAPI. */
export function roomWsUrl(code: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // En dev avec VITE_API_BASE pointant vers un autre host (ex: http://localhost:8000),
  // on aligne le WS sur ce host. Sinon on tape l'origine courante (cas prod via Caddy).
  const base = (import.meta as any).env?.VITE_API_BASE as string | undefined;
  if (base && base.startsWith('http')) {
    const u = new URL(base);
    return `${u.protocol === 'https:' ? 'wss:' : 'ws:'}//${u.host}/ws/rooms/${code.toUpperCase()}`;
  }
  return `${proto}//${window.location.host}/ws/rooms/${code.toUpperCase()}`;
}

// ---------------- Mode Solo / Défi quotidien ----------------

export interface SoloPropositionOut {
  id: string;
  difficulty: string;
  type: string;
  translations: Record<string, any>;
}
export interface SoloDailyState {
  challenge_date: string;
  question_id: string;
  question_prompt: string | null;
  question_type: string | null;
  question_category: string | null;
  total_propositions: number;
  already_played: boolean;
  in_progress: boolean;
  next_available_at: string | null;
  score: number | null;
  duration_ms: number | null;
}
export interface SoloStartOut {
  challenge_date: string;
  index: number;
  total: number;
  score: number;
  duration_ms: number;
  proposition: SoloPropositionOut;
}
export interface SoloRevealedPropositionOut {
  id: string;
  difficulty: string;
  type: string;
  translations: Record<string, any>;
  answer: Record<string, any>;
}
export interface SoloAnswerOut {
  correct: boolean;
  timed_out: boolean;
  time_ms: number;
  index: number;
  total: number;
  score: number;
  duration_ms: number;
  finished: boolean;
  answered_proposition: SoloRevealedPropositionOut | null;
  next: SoloPropositionOut | null;
}
export interface SoloReviewItem {
  proposition_id: string;
  difficulty: string;
  type: string;
  translations: Record<string, any>;
  answer: Record<string, any>;
  submitted: any;
  correct: boolean;
  time_ms: number;
}
export interface SoloReviewOut {
  challenge_date: string;
  question_prompt: string | null;
  score: number;
  total: number;
  duration_ms: number;
  items: SoloReviewItem[];
}
export interface SoloLeaderboardEntry {
  rank: number;
  user_id: number;
  username: string;
  score: number | null;
  duration_ms: number | null;
}
export interface SoloLeaderboardOut {
  period: 'daily' | 'weekly' | 'monthly';
  period_key: string;
  entries: SoloLeaderboardEntry[];
  champion_user_id: number | null;
  champion_username: string | null;
}
export interface BadgeOut {
  type: string;
  period_key: string;
  awarded_at: string;
}
export interface MyBadgesOut {
  badges: BadgeOut[];
}

export const soloApi = {
  getDaily: () => api.get<SoloDailyState>('/solo/daily'),
  start: () => api.post<SoloStartOut>('/solo/daily/start'),
  answer: (propositionId: string, value: any) =>
    api.post<SoloAnswerOut>('/solo/daily/answer', { proposition_id: propositionId, value }),
  leaderboard: (period: 'daily' | 'weekly' | 'monthly', challengeDate?: string) =>
    api.get<SoloLeaderboardOut>(`/solo/leaderboard/${period}${challengeDate ? '?challenge_date=' + challengeDate : ''}`),
  myBadges: () => api.get<MyBadgesOut>('/solo/badges/me'),
  review: (challengeDate?: string) =>
    api.get<SoloReviewOut>(`/solo/daily/review${challengeDate ? '?challenge_date=' + challengeDate : ''}`),
};

// ---------------- Web Push (notification défi du jour) ----------------

export interface VapidPublicKeyOut {
  public_key: string;
}

export const pushApi = {
  vapidPublicKey: () => api.get<VapidPublicKeyOut>('/push/vapid-public-key'),
  subscribe: (endpoint: string, keys: { p256dh: string; auth: string }) =>
    api.post('/push/subscribe', { endpoint, keys }),
  unsubscribe: (endpoint: string) => api.post('/push/unsubscribe', { endpoint }),
};

// ---------------- Mode Duel (défi asynchrone) ----------------

export interface UserSearchOut {
  id: number;
  username: string;
  avatar_url: string | null;
}
export interface DuelRoundSummaryOut {
  round_number: number;
  status: string;
  winner_user_id: number | null;
  current_turn_user_id: number | null;
  turn_deadline_at: string | null;
  my_score: number | null;
  my_duration_ms: number | null;
  opponent_score: number | null;
  opponent_duration_ms: number | null;
}
export interface DuelOut {
  id: number;
  status: string;
  player_a_id: number;
  player_a_username: string;
  player_b_id: number;
  player_b_username: string;
  first_player_id: number | null;
  current_round_number: number;
  rounds_won_a: number;
  rounds_won_b: number;
  winner_id: number | null;
  my_turn: boolean;
  created_at: string;
  finished_at: string | null;
  rounds: DuelRoundSummaryOut[];
  match_score_line: string | null;
  rematch_already_sent: boolean;
}
export interface DuelListItemOut {
  id: number;
  opponent_id: number;
  opponent_username: string;
  status: string;
  rounds_won_me: number;
  rounds_won_opponent: number;
  turn_deadline_at: string | null;
  created_at: string;
  match_score_line: string | null;
}
export interface DuelOpponentStatOut {
  opponent_id: number;
  opponent_username: string;
  wins: number;
  losses: number;
}
export interface DuelStatsOut {
  total_played: number;
  wins: number;
  losses: number;
  opponents: DuelOpponentStatOut[];
}
export interface DuelHeadToHeadMatchOut {
  id: number;
  match_score_line: string;
  i_won: boolean;
  finished_at: string | null;
}
export interface DuelHeadToHeadOut {
  opponent_id: number;
  opponent_username: string;
  wins: number;
  losses: number;
  matches: DuelHeadToHeadMatchOut[];
}
export interface DuelInboxOut {
  incoming_pending: DuelListItemOut[];
  outgoing_pending: DuelListItemOut[];
  my_turn: DuelListItemOut[];
  waiting_on_opponent: DuelListItemOut[];
  finished: DuelListItemOut[];
}
export interface DuelRoundStartOut {
  duel_id: number;
  round_number: number;
  question_prompt: string | null;
  index: number;
  total: number;
  score: number;
  duration_ms: number;
  proposition: SoloPropositionOut;
}
export interface DuelRoundAnswerOut {
  correct: boolean;
  timed_out: boolean;
  time_ms: number;
  index: number;
  total: number;
  score: number;
  duration_ms: number;
  finished: boolean;
  round_completed: boolean;
  match_finished: boolean;
  round_winner_user_id: number | null;
  match_winner_id: number | null;
  answered_proposition: SoloRevealedPropositionOut;
  next: SoloPropositionOut | null;
}

export const duelApi = {
  searchUsers: (q: string) => api.get<UserSearchOut[]>(`/duels/search-users?q=${encodeURIComponent(q)}`),
  challenge: (opponentUsername: string) =>
    api.post<DuelOut>('/duels/challenge', { opponent_username: opponentUsername }),
  rematch: (duelId: number) => api.post<DuelOut>(`/duels/${duelId}/rematch`),
  mine: () => api.get<DuelInboxOut>('/duels/mine'),
  get: (duelId: number) => api.get<DuelOut>(`/duels/${duelId}`),
  accept: (duelId: number) => api.post<DuelOut>(`/duels/${duelId}/accept`),
  decline: (duelId: number) => api.post<DuelOut>(`/duels/${duelId}/decline`),
  startRound: (duelId: number) => api.post<DuelRoundStartOut>(`/duels/${duelId}/round/start`),
  answerRound: (duelId: number, propositionId: string, value: any) =>
    api.post<DuelRoundAnswerOut>(`/duels/${duelId}/round/answer`, { proposition_id: propositionId, value }),
  stats: () => api.get<DuelStatsOut>('/duels/stats'),
  headToHead: (opponentId: number) => api.get<DuelHeadToHeadOut>(`/duels/vs/${opponentId}`),
};

export interface FriendOut {
  friendship_id: number;
  user_id: number;
  username: string;
  avatar_url: string | null;
  status: string;
  is_requester: boolean;
  created_at: string;
}
export interface FriendsListOut {
  accepted: FriendOut[];
  incoming_pending: FriendOut[];
  outgoing_pending: FriendOut[];
}

export const contactsApi = {
  request: (username: string) => api.post<FriendOut>('/contacts/request', { username }),
  accept: (friendshipId: number) => api.post<FriendOut>(`/contacts/${friendshipId}/accept`),
  decline: (friendshipId: number) => api.post<FriendOut>(`/contacts/${friendshipId}/decline`),
  remove: (friendshipId: number) => api.delete(`/contacts/${friendshipId}`),
  mine: () => api.get<FriendsListOut>('/contacts/mine'),
};

export const questionsApi = {
  list: (status?: 'active' | 'pending') =>
    api.get<any[]>(`/questions${status ? '?status=' + status : ''}`),
  get: (id: string) => api.get<any>(`/questions/${id}`),
  create: (data: any) => api.post<any>('/questions', data),
  update: (id: string, data: any) => api.put<any>(`/questions/${id}`, data),
  validate: (id: string, edits?: any) =>
    api.post<any>(`/questions/${id}/validate`, edits ? { edits } : {}),
  remove: (id: string) => api.delete(`/questions/${id}`),
  addProposition: (qid: string, prop: any) =>
    api.post<any>(`/questions/${qid}/propositions`, prop),
  validateProposition: (pid: string) =>
    api.post<any>(`/questions/propositions/${pid}/validate`),
  removeProposition: (pid: string) =>
    api.delete(`/questions/propositions/${pid}`),
};
